import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { type StoreApi } from 'zustand';

import { stateClient } from '@dynatrace-sdk/client-state';

import { type AppStateStore, createAppStateStore } from './app-state-store.js';
import {
  AppStateContext,
  queryClientConfig,
  type AppStateContextValue,
} from './AppStateContext.js';

/**
 * Props for {@link AppStateProvider}.
 */
export interface AppStateProviderProps {
  children: ReactNode;
  /**
   * Whether to use user-scoped or app-scoped state.
   * - `'user'`: state is stored per user per app (default is `'user'`)
   * - `'app'`: state is shared across all users of the app
   */
  scope?: 'user' | 'app';
  /**
   * Called when the initial state fetch or a mutation fails.
   */
  onError?: (error: Error) => void;
}

function AppStateLoader() {
  const { store, scope, queryClient } = useContext(AppStateContext);

  const { data, error } = useQuery(
    {
      queryKey: ['app-state', scope],
      queryFn: ({ signal }) => {
        if (scope === 'user') {
          return stateClient.getUserAppStates({
            addFields: 'value',
            abortSignal: signal,
          });
        }
        return stateClient.getAppStates({
          addFields: 'value',
          abortSignal: signal,
        });
      },
      throwOnError: false,
    },
    queryClient,
  );

  useLayoutEffect(() => {
    if (data !== undefined) {
      const flattened: Record<string, string> = {};
      for (const item of data) {
        if (item.value !== undefined) {
          flattened[item.key] = item.value;
        }
      }
      store.setState({ data: flattened, isLoading: false, error: null });
    }
  }, [data, store]);

  useLayoutEffect(() => {
    if (error !== null) {
      // AbortErrors come from intentional request cancellation (navigation,
      // unmount) — not real failures. Clear the loading state but don't
      // propagate to store.error or the onError callback.
      if (error.name === 'AbortError') {
        store.setState({ isLoading: false });
        return;
      }
      store.setState({ isLoading: false, error: error as Error });
      // Read onError imperatively — it lives in the Zustand store to avoid
      // putting a potentially-unstable function reference in React context.
      store.getState().onError?.(error as Error);
    }
  }, [error, store]);

  return null;
}

/**
 * Provides reactive access to Dynatrace app state for its subtree.
 *
 * Wrap your app (or a section of it) in `<AppStateProvider>` and use
 * `_useUserAppState` / `_useAppState` to read and write persisted state keys.
 */
export function AppStateProvider({
  children,
  scope = 'user',
  onError,
}: AppStateProviderProps) {
  const storeRef = useRef<StoreApi<AppStateStore> | null>(null);
  const queryClientRef = useRef<QueryClient | null>(null);

  let store = storeRef.current;
  let queryClient = queryClientRef.current;

  if (store === null || queryClient === null) {
    store = createAppStateStore();
    queryClient = new QueryClient(queryClientConfig);
    storeRef.current = store;
    queryClientRef.current = queryClient;
  }

  // Keep onError in the Zustand store so hooks can call it imperatively
  // without putting a potentially-unstable function reference in React context.
  useLayoutEffect(() => {
    store.setState({ onError });
  }, [onError, store]);

  const contextValue = useMemo<AppStateContextValue>(
    () => ({ store, scope, queryClient }),
    // store and queryClient are stable refs; only scope can change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope],
  );

  return (
    <AppStateContext.Provider value={contextValue}>
      <AppStateLoader />
      {children}
    </AppStateContext.Provider>
  );
}
